import { Component, type ErrorInfo, type ReactNode } from "react";

import { captureDappUiException } from "./client";

export interface DappUiErrorBoundaryProps {
  children: ReactNode;
  /** Rendered when a child throws; defaults to rendering nothing. */
  fallback?: ReactNode;
  /** Identifies which popup/root failed, e.g. "app-launcher". */
  name?: string;
}

interface DappUiErrorBoundaryState {
  hasError: boolean;
}

/**
 * Reports to the isolated dapp-ui Sentry client instead of the global hub —
 * Sentry.ErrorBoundary would send to whichever client the host app installed.
 */
export class DappUiErrorBoundary extends Component<
  DappUiErrorBoundaryProps,
  DappUiErrorBoundaryState
> {
  state: DappUiErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): DappUiErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureDappUiException(error, {
      componentStack: info.componentStack ?? undefined,
      boundary: this.props.name ?? "dapp-ui",
    });
  }

  render(): ReactNode {
    if (this.state.hasError) return this.props.fallback ?? null;
    return this.props.children;
  }
}
